import {
    STC_VARIANT,
    allVariants,
    matrixRouteRoot,
    openFormsSignatories,
    revisionIdPath,
    revisionNumberPath,
    revisionSnapshotsPath,
    signatoriesPath,
    testersPath
} from "../_variants/index.js";
import {
    addTester,
    approveSignatoryApplication,
    editTester,
    makeActive,
    makeInactive,
    updateApprovedFunctions,
    updateEmployee,
    userSignOffChanges
} from "./actions.js";
import { approvedSignatoryItem, resolveSignatoryLocation } from "./signatoryIdentity.common.js";
import { auth, db } from "../../../db.js";
import {
    files,
    files2,
    formfour,
    limitations,
    notes,
    panels,
    questions,
    questions2,
    records,
    sampleSignature,
    testersFiles
} from "./internalAttachables";
import { signatoryDetailTitle, signatorySelfAppSwitcher, signatoryViewingLatest } from "./signatoryRouteMeta.js";

import ItemListView from "../../../components/ItemListView/ItemListView.vue";
import ItemView from "../../../components/ItemView/ItemView.vue";
import SignatoryMatrix from "./SignatoryMatrix.vue";
import settings from "./Settings/routes.js"
import store from "../../../store.js";

// Function to generate the HTML span elements based on the input parameters
const span = (bool, totalPoints, threshold, string, item) => {
    // Check if the item is exempt from points requirements
    const exempt = item.PointsExempt || false;

    // Create the exempt string based on the exempt flag
    const exemptString = exempt || totalPoints - threshold >= 0 ? '' : `<span class="font-medium text-orange">&nbsp; - Point threshold of ${threshold} not met - Additional ${(totalPoints - threshold) * -1} points needed</span>`;

    return bool ?
        `<span class="a font-bold text-green">${string}${exemptString}</span>` :
        `<span class="b font-bold text-gray-300">${string}</span>`;
};

/**
 * Build the list + detail vue-router routes for the Signatory Matrix
 * matching the supplied variant.
 *
 * Routes include:
 * - Self view (current user's own signatory record)
 * - Signatories list (approved matrix)
 * - Unapproved changes view (next revision preview)
 * - Form4 Holders (Post Holders)
 * - New Signatories (applications inbox)
 * - Signatory detail
 * - New Signatory application detail
 * - Testers list
 * - Tester detail
 *
 * Each detail route's `meta.formMetadata.ParentType` carries the variant's
 * parentBackref. Matrix actions use explicit variant references (STC_VARIANT,
 * MILITARY_VARIANT) rather than deriving the variant from ParentType like
 * Database actions do.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeSignatoryMatrixRoutes = (variant) => {
    const isMilitary = variant.key === "military";
    const labelPrefix = isMilitary ? "Military " : "";
    const matrixRoot = matrixRouteRoot(variant);
    const signatoriesDataPath = signatoriesPath(variant);
    const testersDataPath = testersPath(variant);
    const openFormsPath = openFormsSignatories(variant);
    
    // ParentType for this variant's matrix (e.g. "SignatoryMatrix" or "MilitarySignatoryMatrix")
    const matrixParentType = variant.collectionPrefix + "SignatoryMatrix";

    
    
    // Security paths follow the variant: STC uses Matrices.Reviewer/Write, Military uses Military.Reviewer/Write
    const securityReviewer = isMilitary ? "Military.Reviewer" : "Matrices.Reviewer";
    const securityWrite = isMilitary ? "Military.Write" : "Matrices.Write";
    return [{
    "path": `${matrixRoot}/Self`,
    "component": ItemView ,
    "name": `${labelPrefix} My Signatory Record`,
    "meta": {
        "app": variant.app,
        "name": `${labelPrefix} Signatory`,
        "appSwitcher": (user) => signatorySelfAppSwitcher(user, store.state.tenantClaim),
        "appSwitcherPosition": variant.appSwitcherPosition,
        "sidenav": true,
        "sidenavPosition": 0,
        "sidenavSection": "My Record",
        "icon": variant.appIcon,
        "hideBackButton": true,
        "addToRecents": false,
        "storeData": [
            "Matrices/SignatoryMatrix/InterviewQuestions",
            "Matrices/SignatoryMatrix/Panels",
            "Matrices/SignatoryMatrix/ApprovedFunctions",
            "Matrices/SignatoryMatrix/Helper"
        ],
        async dataPathOverride(route, { store, db }) {
            const uid = auth.currentUser?.uid;
            if (!uid) return signatoriesDataPath; // fallback path, will be empty
            
            // Post-#117: records use uid keys. AccountID/ID fields should equal the key.
            return `${signatoriesDataPath}/${uid}`;
        },
        // itemOverride(item, { store }) {
        //     if (!item) return item;
        //     return { ...item, TrainingRecords: store.state[`Userdata/${item.AccountID}/Training/Records`] || {} };
        // },
        onRouteEnter({ store }) {
            store.commit("save", { "sigMatrixViewingNew": true });
        },
        async getLinkedData({ pageItem }) {
            return {
                "TrainingRecords": await db.store(`Userdata/${pageItem.AccountID}/Training/Records`, store),
            };
        },
        title(item) {
            return `My Signatory Record: ${item?.FirstName} ${item?.LastName}`;
        },
        "attachedItems": {
            "Additional Details": {
                "attachables": [panels, limitations, files, notes, questions]
            }
        },
        "dataFields": [{
            "label": "TSR Number & Name",
            compute({ item }) {
                return `${item.CSR} - ${item.FirstName} ${item.LastName}`;
            }
        },
        {
            "label": "Email",
            "key": "Email"
        },
        {
            "label": "Position Held / Job Title",
            "key": "Position"
        },
        {
            "label": "User Sign-Off on Entry Changes",
            "class": "md:col-span-2",
            compute({ item }) {
                if (item?.UserSignOff) {
                    const dateStr = item.UserSignOffDate
                        ? new Date(item.UserSignOffDate).toLocaleDateString("en-gb")
                        : "";
                    return `<span class="font-bold text-green">Signed Off</span>${dateStr ? ` <span class="text-gray-500">(${dateStr})</span>` : ""}`;
                }
                return `<span class="font-bold text-orange">Not signed off</span> <span class="text-gray-500">(the user must sign off before HoD/HoA can accept the next revision)</span>`;
            }
        },
        {
            "label": "Approved Functions",
            "class": "md:col-span-3",
            "grow": true,
            compute({ item, store }) {
                const totalPoints = Object.values(item.InterviewQuestions || {}).reduce((acc, q) => acc + (parseFloat(q.Points || 0) || 0), 0);
                const spans = Object.values(store.state["Matrices/SignatoryMatrix/ApprovedFunctions"] || {}).map(i => {
                    return span(
                        item?.ApprovedFunctions ? !!item.ApprovedFunctions[i.ID] : false,
                        totalPoints,
                        parseInt(i.Threshold) || 0,
                        `${i.Name}${i.FormFourFunction ? " (Post Holder)" : ""}`,
                        item
                    );
                }).sort();
                return `
                    <div class="grid grid-cols-1 md:grid-cols-3">
                    ${spans.join("")}
                    </div>
                `;
            }
        }],
        "menuActions": [{
            "label": "Sign Off Changes to My Entry",
            "action": userSignOffChanges,
            "break": true,
            show({ pageItem }) {
                if (pageItem?.UserSignOff === true) {
                    return [false, "You have already signed off the current changes to your entry."];
                }
                return [true];
            }
        }],
        show(user ,{ store  }) {

            // const user = store.state.user;
            const tenantClaim = store.state.tenantClaim;

            if (!user || !tenantClaim) {
                console.warn("User or tenantClaim is missing:", { user, tenantClaim });
                return false;
            }
            if( !tenantClaim?.signatory && !tenantClaim?.signatory_military) {
                console.warn("User does not have signatory or signatory_military claim:", { user, tenantClaim });
                return false;
            }

            const hasReviewerAccess = isMilitary ? (user?.Security?.Military?.Reviewer || tenantClaim?.signatory_military) : (user?.Security?.Matrices?.Reviewer || tenantClaim?.signatory);
            if (hasReviewerAccess === false || hasReviewerAccess === undefined) return false;
            
            // For external users, require matching class on this tenant
            const authUser = auth.currentUser;
            if (!authUser) return false;
            
            try {
                
                // Check if user has the correct signatory class for this variant
                const requiredClass = isMilitary ? 'signatory_military' : 'signatory';
                if (!tenantClaim?.[requiredClass]) {
                    return false
                }
            } catch (e) {
                console.error("Failed to check signatory claims:", e);
                return false;
            }
            
            return true;
        },
        "formMetadata": {
            "ParentType": matrixParentType
        }
    }
},
    {
    "path": `${matrixRoot}/Signatories`,
    "component": SignatoryMatrix,
    "name": `${labelPrefix}Approved Signatory Matrix`,
    "meta": {
        "storeData": [
            "Matrices/SignatoryMatrix/InterviewQuestions",
            "Matrices/SignatoryMatrix/Panels",
            "Matrices/SignatoryMatrix/ApprovedFunctions"
        ],
        "name": `${labelPrefix}Current Signatory Matrix`,
        "app": variant.app,
        "appSwitcher": true,
        "variant": variant.key,

        "sideNav": true,
        "sideNavPosition": 1,
        // "appSwitcher": isMilitary,
        "appSwitcherPosition": variant.appSwitcherPosition,
        "icon": variant.appIcon,
        "security": variant.security,
        "sideNavSection": isMilitary ? "Military Signatory Matrix" : "Signatory Matrix",
        "latest": false,
        "formMetadata": {
            "ParentType": matrixParentType
        }
    },
    lock(user) {
        const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
        return [!hasReviewerAccess, "Cannot view this area"]
    }
}, {
    "path": `${matrixRoot}/UnapprovedSignatoriesChanges`,
    "component": SignatoryMatrix,
    "name": `${labelPrefix}Unapproved Signatory Matrix (Next Issue)`,
    "meta": {
        "storeData": [
            "Matrices/SignatoryMatrix/InterviewQuestions",
            "Matrices/SignatoryMatrix/Panels",
            "Matrices/SignatoryMatrix/ApprovedFunctions"
        ],
        "name": `${labelPrefix}Unapproved Changes (Next Issue)`,
        "app": variant.app,
        "sideNav": true,
        "appSwitcher": false,
        "appSwitcherPosition": variant.appSwitcherPosition,
        "icon": variant.appIcon,
        "security": securityReviewer,
        "sideNavSection": isMilitary ? "Military Signatory Matrix" : "Signatory Matrix",
        "latest": true,
        "formMetadata": {
            "ParentType": matrixParentType
        },
        lock(user) {
            const hasWriteAccess = isMilitary ? user?.Security?.Military?.Write : user?.Security?.Matrices?.Write;
            return [!hasWriteAccess, "Cannot view this area"]
        }
    },
    lock(user) {
        const hasWriteAccess = isMilitary ? user?.Security?.Military?.Write : user?.Security?.Matrices?.Write;
        return [!hasWriteAccess, "Cannot view this area"]
    }
}, {
    "path": `${matrixRoot}/Form4Holders`,
    "component": { ...ItemListView },
    "name": `${labelPrefix}Post Holders`,
    "meta": {
        "storeData": ["Matrices/SignatoryMatrix/InterviewQuestions", "Matrices/SignatoryMatrix/Panels", "Matrices/SignatoryMatrix/ApprovedFunctions"],
        "app": variant.app,
        "sideNav": true,
        "icon": variant.appIcon,
        "name": `${labelPrefix}Post Holders`,
        "sideNavSection": isMilitary ? "Military Signatory Matrix" : "Signatory Matrix",
        "tableSettings": `${variant.collectionPrefix}Fourholders`,
        "selectionRoute": `${matrixRoot.substring(1)}/Signatories/{ID}`,
        "dataPath": signatoriesDataPath,
        filter(item) {
            return Object.keys(item.ApprovedFunctions || {}).some(key => {
                return Object.values(store.state["Matrices/SignatoryMatrix/ApprovedFunctions"]).filter(i => i.FormFourFunction).map(i => i.ID).includes(key);
            });
        },
        lock(user) {
            const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
            return [!hasReviewerAccess, "Cannot view this area"]
        },
        "formMetadata": {
            "ParentType": matrixParentType
        }
    }
}, 
// {
//     "path": `${matrixRoot}/NewSignatories`,
//     "component": { ...ItemListView },
//     "name": `${labelPrefix}Signatory Applications`,
//     "meta": {
//         "storeData": [
//             "Matrices/SignatoryMatrix/InterviewQuestions",
//             "Matrices/SignatoryMatrix/Panels",
//             revisionNumberPath(variant),
//             "Matrices/SignatoryMatrix/ApprovedFunctions",
//             "Matrices/SignatoryMatrix/InterviewQuestions",
//             "Matrices/SignatoryMatrix/Helper"
//         ],
//         "app": variant.app,
//         "sideNav": true,
//         "appSwitcher": false,
//         "appSwitcherPosition": variant.appSwitcherPosition,
//         "name": `${labelPrefix}Signatory Applications`,
//         "sideNavSection": isMilitary ? "Military Signatory Matrix" : "Signatory Matrix",
//         "dataPath": openFormsPath,
//         "tableSettings": `${variant.collectionPrefix}SignatoryApplications`,
//         "selectionRoute": `${matrixRoot.substring(1)}/NewSignatories/{ID}`,
//         map(item) {
//             return {
//                 ...item,
//                 "Role": store.state["Matrices/SignatoryMatrix/ApprovedFunctions"][item?.Role]?.Name
//             }
//         },
//         filter(item) {
//             return item.Completed;
//         },
//         lock(user) {
//             const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
//             return [!hasReviewerAccess, "Cannot view this area"]
//         },
//         "formMetadata": {
//             "ParentType": matrixParentType
//         }
//     }
// }, 
{
    "path": `${matrixRoot}/Signatories/:id/:showLatest?`,
    "component": ItemView,
    "name": `${labelPrefix}Signatory`,
    "meta": {
        "app": variant.app,
        "name": `${labelPrefix}Signatory`,
        "dataPath": signatoriesDataPath,
        "storeData": [signatoriesDataPath,
                revisionNumberPath(variant),
            "Matrices/SignatoryMatrix/InterviewQuestions",
            "Matrices/SignatoryMatrix/Panels",
            "Matrices/SignatoryMatrix/ApprovedFunctions",
            "Matrices/SignatoryMatrix/Helper"
        ],
        async dataPathOverride(route, { db }) {
            return resolveSignatoryLocation(db, signatoriesDataPath, route.params.id);
        },
        itemOverride(item, context = {}) {
            const store = context.store;
            const viewingLatest = signatoryViewingLatest(context);
            const revisionStateKey = isMilitary ? "currentMilitarySignatoryMatrixRevision" : "currentSignatoryMatrixRevision";
            const displayed = viewingLatest ? item : approvedSignatoryItem(item, store?.state?.[revisionStateKey]);
            if (!displayed) return displayed;
            return { ...displayed, TrainingRecords: store?.state?.[`Userdata/${displayed.AccountID}/Training/Records`] || {} };
        },
        onRouteEnter(context = {}) {
            context.store?.commit("save", { "sigMatrixViewingNew": signatoryViewingLatest(context) });
        },
        async getLinkedData({pageItem} ) {
    
            return {
                "TrainingRecords": await db.store(`Userdata/${pageItem.AccountID}/Training/Records`, store),
            }
        },
        async onBeforeMount() {
            let id = await db.watch(
                revisionIdPath(variant),
                store
            );
            await db.watch(
                `${revisionSnapshotsPath(variant)}/${id}`,
                store,
                isMilitary ? "currentMilitarySignatoryMatrixRevision" : "currentSignatoryMatrixRevision"
            );

            // return;
        },
        async lock(user, { store, to, db }) {
            const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
            try {
                const itemLocation = await resolveSignatoryLocation(db, signatoriesDataPath, to.params.id);
                const item = await db.store(itemLocation, store);
                if (!item || (!hasReviewerAccess && (!store.getters.userID || store.getters.userID !== item.AccountID))) {
                    return [true, "Cannot view this area"];
                }
                if (to.params.showLatest !== "true") {
                    const revisionID = await db.once(revisionIdPath(variant));
                    if (typeof revisionID !== "string" || !revisionID || /[.#$\[\]/\u0000-\u001f\u007f]/.test(revisionID)) {
                        return [true, "Approved signatory record is unavailable"];
                    }
                    const revision = await db.once(`${revisionSnapshotsPath(variant)}/${revisionID}`);
                    if (!approvedSignatoryItem(item, revision)) return [true, "Approved signatory record is unavailable"];
                }
                return [false];
            } catch {
                return [true, "Cannot resolve this signatory record"];
            }
        },
        "formMetadata": {
            "ParentType": matrixParentType
        },
        title(item, { router } = {}) {
            return signatoryDetailTitle(item, { router });
        },
        "attachedItems": {
            "Additional Details": {
                "attachables": [panels, limitations, files, notes, questions, sampleSignature]
            },
            "Form Four Holder Data": {
                "attachables": [formfour]
            },
            "Staff Training and Resposibilties": {
                "attachables": [records]
            }
        },
        "dataFields": [{
            "label": "TSR Number & Name",
            compute({ item }) {
                return `${item.CSR} - ${item.FirstName} ${item.LastName}`;
            }
        },
        {
            "label": "Email",
            "key": "Email"
        },
        {
            "label": "Position Held / Job Title",
            "key": "Position"
        },
        {
            "label": "User Sign-Off on Entry Changes",
            "class": "md:col-span-2",
            compute({ item }) {
                if (item?.UserSignOff) {
                    const dateStr = item.UserSignOffDate
                        ? new Date(item.UserSignOffDate).toLocaleDateString("en-gb")
                        : "";
                    return `<span class="font-bold text-green">Signed Off</span>${dateStr ? ` <span class="text-gray-500">(${dateStr})</span>` : ""}`;
                }
                return `<span class="font-bold text-orange">Not signed off</span> <span class="text-gray-500">(the user must sign off before HoD/HoA can accept the next revision)</span>`;
            }
        },
        {
            "label": "Approved Functions",
            "class": "md:col-span-3",
            "grow": true,
            compute({ item, store }) {

                // Calculate the total points for all InterviewQuestions
                const totalPoints = Object.values(item.InterviewQuestions || {}).reduce((acc, q) => acc + (parseFloat(q.Points || 0) || 0), 0);

                // Generate the HTML span elements for each approved function
                const spans = Object.values(store.state["Matrices/SignatoryMatrix/ApprovedFunctions"]).map(i => {
                    return span(
                        // Check if the function is approved for this item
                        item?.ApprovedFunctions ? !!item.ApprovedFunctions[i.ID] : false,
                        totalPoints,
                        // Parse the threshold as an integer or default to 0
                        parseInt(i.Threshold) || 0,
                        `${i.Name}${i.FormFourFunction ? " (Post Holder)" : ""}`,
                        item
                    );
                }).sort();

                // Return the final HTML string with the generated spans inside a grid
                return `
                    <div class="grid grid-cols-1 md:grid-cols-3">
                    ${spans.join("")}
                    </div>
                `;
            }
        }],
        "menuActions": [{
            "label": "Update Personal Data",
            "action": updateEmployee,
            show({ store }) {
                if (store?.state?.sigMatrixViewingNew === false) {
                    return [false, "You cannot update this when viewing a revision"];
                } else {
                    const hasReviewerAccess = isMilitary ? store?.state?.user?.Security?.Military?.Reviewer : store?.state?.user?.Security?.Matrices?.Reviewer;
                    return [hasReviewerAccess === true, "You cannot update this"]
                }
            }
        }, {
            "label": "Update Approved Functions",
            "action": updateApprovedFunctions,
            show({ store }) {
                if (store?.state?.sigMatrixViewingNew === false) {
                    return [false, "You cannot update this when viewing a revision"];
                } else {
                    const hasReviewerAccess = isMilitary ? store?.state?.user?.Security?.Military?.Reviewer : store?.state?.user?.Security?.Matrices?.Reviewer;
                    return [hasReviewerAccess === true, "You cannot update this"]
                };
            }
        }, {
            "label": "Sign Off Changes to My Entry",
            "action": userSignOffChanges,
            "break": true,
            show({ store, pageItem }) {
                const viewingNew = store?.state?.sigMatrixViewingNew === true;
                const isOwner = pageItem?.AccountID === store.getters.userID;
                const alreadySigned = pageItem?.UserSignOff === true;
                if (!viewingNew) {
                    return [false, "Sign-off is only available when viewing the unapproved/latest changes."];
                }
                if (!isOwner) {
                    return [false, "Only the owner of this entry can sign off changes to it."];
                }
                if (alreadySigned) {
                    return [false, "You have already signed off the current changes to your entry."];
                }
                return [true];
            }
        }, {
            "label": "Make Active",
            "action": makeActive,
            "break": true,
            show({ pageItem }) { return [pageItem.Cancelled === true, "This signatory is already active"] }
        }, {
            "label": "Make Inactive",
            "action": makeInactive,
            show({ pageItem }) {
                const hasReviewerAccess = isMilitary ? store?.state?.user?.Security?.Military?.Reviewer : store?.state?.user?.Security?.Matrices?.Reviewer;
                if (hasReviewerAccess !== true) {
                    return [false, "You cannot update this"];
                }
                return [pageItem.Cancelled !== true, "This signatory is already inactive"];
            }

        }]
    }
}, {
    "path": `${matrixRoot}/NewSignatories/:id`,
    "component": ItemView,
    "name": `${labelPrefix}NewSignatory`,
    "meta": {
        "app": variant.app,
        "name": `${labelPrefix}NewSignatory`,
        "dataPathOverride": `${openFormsPath}/:id`,
        "storeData": [
            "Matrices/SignatoryMatrix/InterviewQuestions",
            "Matrices/SignatoryMatrix/Panels",
            "Matrices/SignatoryMatrix/ApprovedFunctions",
            "Matrices/SignatoryMatrix/Helper"
        ],
        title(item) {
            return `Employee: ${item?.FirstName} ${item?.LastName}`
        },
        "attachedItems": {
            "Additional Details": {
                "attachables": [questions2, files2]
            }
        },
        "dataFields": [{
            "label": "Name",
            compute({ item }) {
                return `${item.FirstName} ${item.LastName}`;
            }
        },
        {
            "label": "Email",
            "key": "Email"
        },
        {
            "label": "Role",
            compute({ pageItem, store }) {
                return store.state["Matrices/SignatoryMatrix/ApprovedFunctions"][pageItem.Role].Name
            }
        }],
        "menuActions": [{
            "label": "Approve Signatory Application",
            "action": approveSignatoryApplication,
            show({ store }) {
                const hasReviewerAccess = isMilitary ? store.state.user?.Security?.Military?.Reviewer : store.state.user?.Security?.Matrices?.Reviewer;
                return [hasReviewerAccess === true, "Must be a HoD to approve this application"];
            }
        }],
        lock(user) {
            const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
            return [!hasReviewerAccess, "Cannot view this area"]
        },
        "formMetadata": {
            "ParentType": matrixParentType
        }
    }
}, {
    "path": `${matrixRoot}/Testers`,
    "component": { ...ItemListView },
    "name": `${labelPrefix}Test Witnesses`,
    "meta": {
        "app": variant.app,
        "sideNav": true,
        "sideNavPosition": 2,
        "icon": "clipboard-list",
        "name": `${labelPrefix}Test Witnesses`,
        "sideNavSection": isMilitary ? "Military Testers" : "Testers",
        "tableSettings": `${variant.collectionPrefix}Testers`,
        "selectionRoute": `${matrixRoot.substring(1)}/Testers/{ID}`,
        "dataPath": testersDataPath,
        "menuActions": [{
            "label": "Add New Tester",
            "action": addTester,
        }],
        lock(user) {
            const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
            return [!hasReviewerAccess, "Cannot view this area"]
        },
        "formMetadata": {
            "ParentType": matrixParentType
        }

    },
    lock(user) {
        const hasReviewerAccess = isMilitary ? user?.Security?.Military?.Reviewer : user?.Security?.Matrices?.Reviewer;
        return [!hasReviewerAccess, "Cannot view this area"]
    }
}, {
    "path": `${matrixRoot}/Testers/:id`,
    "component": { ...ItemView },
    "name": `${labelPrefix}Test Witness`,
    "meta": {
        "sideNav": false,
        "app": variant.app,
        "name": `${labelPrefix}Test Witness`,
        "title": () => "Tester",
        "dataPath": testersDataPath,
        "dataFields": [{
            "label": "Name",
            "key": "Name"
        }, {
            "label": "Types",
            "key": "Types",
        }],
        "menuActions": [{
            "label": "Edit Tester",
            "action": editTester,
        }],
        "attachedItems": {
            "Additional Details": {
                "attachables": [testersFiles]
            }
        },
        "formMetadata": {
            "ParentType": matrixParentType
        }
    }
}];
};

// Default export: all variant routes when military is enabled (#14).
//
// Military routes are now registered alongside STC when isMilitaryEnabled() returns true.
// Grid/attachables load variant-specific instance trees via explicit STC_VARIANT / MILITARY_VARIANT
// resolution from meta.formMetadata.ParentType. Settings remains shared on /Matrices/SignatoryMatrix/Settings.
//
// #15 (this PR) adds OpenForms/Self/authState variant support. #16 will add RTDB rules.
export default [...allVariants().flatMap(makeSignatoryMatrixRoutes), ...settings];

// Convenience re-export for callers that explicitly want only the STC routes
// (e.g. legacy imports).
export const stcSignatoryMatrixRoutes = makeSignatoryMatrixRoutes(STC_VARIANT);
